import { Link } from "react-router-dom";
import { testimonials, stats } from "@/mocks/gossip";
import Breadcrumb from "@/components/base/Breadcrumb";
import Seo from "@/components/feature/Seo";

const PRINCIPLES = [
  {
    icon: "ri-link",
    title: "每条整理都有出处",
    desc: "公开影像、账号发言与从业者表态都会分类标注，无法确认的部分明确写清不确定，不做主观推断。",
  },
  {
    icon: "ri-time-line",
    title: "按时间线还原过程",
    desc: "热点不是凭空出现的。我们把关键节点按时间排开，让你自己看清事件是怎么一步步走到今天的。",
  },
  {
    icon: "ri-shield-check-line",
    title: "不做针对个人的定性",
    desc: "内容宗旨是提供可追溯的阅读参考，而不是给谁下结论。涉及争议时，优先呈现多方公开表述。",
  },
  {
    icon: "ri-refresh-line",
    title: "有误必改，改动留痕",
    desc: "收到更正说明并核实后，我们会尽快修订内容，并在页面标注更新记录，保证信息可追溯。",
  },
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 看料吃瓜社",
    url: "https://kanliaochigua.example/about",
    inLanguage: "zh-CN",
    description:
      "看料吃瓜社的品牌介绍、网站定位、发展理念、内容原则与联系方式。",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "看料吃瓜社",
    url: "https://kanliaochigua.example/",
    email: "contact@kanliaochigua.example",
    description:
      "专注娱乐热点与网络舆论话题整理的中文内容站点，坚持标注信息来源与更新时间。",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://kanliaochigua.example/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://kanliaochigua.example/about" },
    ],
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="关于我们 | 看料吃瓜社的内容原则与定位"
        description="了解看料吃瓜社的品牌介绍、网站定位、发展理念与内容原则。我们坚持为每条瓜料标注信息来源、按时间线还原过程，并提供可追溯的更正渠道。"
        keywords="看料吃瓜社,关于我们,内容原则,信息来源,时间线整理"
        path="/about"
        jsonLd={jsonLd}
      />

      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            关于看料吃瓜社
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            一个把散落热点整理成可读时间线的中文内容站点。我们相信，比起抢一时的情绪，把来龙去脉讲清楚更有价值。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-14 md:mb-20">
          <div className="lg:col-span-6">
            <span className="font-label text-xs tracking-[0.2em] text-primary-600 uppercase">Brand</span>
            <h2 className="mt-2 font-heading text-xl md:text-3xl font-bold text-foreground-950 leading-tight">
              品牌介绍
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
              看料吃瓜社起步于一群喜欢追热点的普通读者。最初只是几个人把当天的公开信息整理成一份简单的对照表，后来慢慢形成了固定的板块：热门大瓜、劲爆猛料与深水硬核。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
              我们不做「转发即结论」的内容，而是把能核实的部分摊开，把不能确认的部分写清楚。这样做的直接好处是：读者读完能自己判断，而不是被带着走。
            </p>
          </div>
          <div className="lg:col-span-6">
            <figure className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20editorial%20desk%20scene%20with%20notebook%20timeline%20sketch%2C%20amber%20and%20deep%20olive%20tones%2C%20soft%20natural%20window%20light%2C%20cinematic%20magazine%20aesthetic%2C%20no%20people%2C%20clean%20minimal%20composition&width=1000&height=700&seq=guatian-about-01&orientation=landscape"
                alt="看料吃瓜社内容工作室场景抽象图"
                title="看料吃瓜社 - 品牌介绍"
                className="w-full h-full object-cover"
              />
            </figure>
          </div>
        </div>

        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-14 md:mb-20">
          {stats.map((item) => (
            <div key={item.label} className="p-5 md:p-6 bg-background-100 border border-background-200 rounded-lg">
              <dd className="font-heading text-2xl md:text-3xl font-bold text-primary-600">
                {item.value}
                <span className="text-sm font-label text-foreground-500 ml-1">{item.unit}</span>
              </dd>
              <dt className="mt-2 font-label text-xs text-foreground-600">{item.label}</dt>
            </div>
          ))}
        </dl>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-14 md:mb-20">
          <section aria-labelledby="position-heading" className="p-6 md:p-8 bg-background-50 border border-background-200 rounded-lg">
            <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center text-xl mb-4">
              <i className="ri-compass-3-line"></i>
            </span>
            <h2 id="position-heading" className="font-heading text-xl font-bold text-foreground-950 mb-3">
              网站定位
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">
              面向关注娱乐与网络舆论走向的中文用户，做一份「一次看够」的瓜源汇总。你不需要在多个平台之间来回跳转，只需要在这里看清楚一件事的起因、经过与当前的公开状态。
            </p>
          </section>
          <section aria-labelledby="vision-heading" className="p-6 md:p-8 bg-background-50 border border-background-200 rounded-lg">
            <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl mb-4">
              <i className="ri-lightbulb-line"></i>
            </span>
            <h2 id="vision-heading" className="font-heading text-xl font-bold text-foreground-950 mb-3">
              发展理念
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">
              内容的寿命，取决于它的准确度。我们宁可晚半天发布一条整理，也不愿意为了速度省略核实。长期来看，读者记住的从来不是谁抢到了第一，而是谁讲得最清楚。
            </p>
          </section>
        </div>

        <section className="mb-14 md:mb-20" aria-labelledby="principle-heading">
          <h2 id="principle-heading" className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-2">
            内容原则
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-2xl">
            这四条是我们写每一条瓜料时都会过一遍的标准，也是接受读者监督的依据。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PRINCIPLES.map((item, index) => (
              <div key={item.title} className="flex gap-4 p-5 md:p-6 bg-background-100 border border-background-200 rounded-lg">
                <span className="w-10 h-10 shrink-0 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-lg">
                  <i className={item.icon}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1.5">
                    <span className="font-label text-xs text-primary-600 mr-2">0{index + 1}</span>
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14 md:mb-20" aria-labelledby="about-voice-heading">
          <h2 id="about-voice-heading" className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-8">
            读者与从业者的评价
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((item) => (
              <figure key={item.name} className="p-5 bg-background-50 border border-background-200 rounded-lg">
                <i className="ri-double-quotes-l text-2xl text-accent-400"></i>
                <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 pt-4 border-t border-background-200 font-label text-xs text-foreground-500">
                  {item.name} · {item.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="p-6 md:p-10 bg-secondary-950 rounded-lg" aria-labelledby="about-contact-heading">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
            <div>
              <h2 id="about-contact-heading" className="font-heading text-xl md:text-2xl font-bold text-background-50">
                联系我们
              </h2>
              <p className="mt-3 text-sm text-background-300/90 leading-relaxed">
                线索投稿、内容更正与内容合作，都可以通过联系表单或邮箱与我们沟通。我们通常会在两个工作日内回复。
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-label font-medium rounded-md bg-accent-500 text-foreground-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line"></i>
                打开联系表单
              </Link>
              <a
                href="mailto:contact@kanliaochigua.example"
                rel="nofollow"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-label font-medium rounded-md bg-background-50/10 border border-background-50/30 text-background-50 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                contact@kanliaochigua.example
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}